import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const COMPLETE_PAGE_PATH = "/registrations/complete";
const COMPLETE_PAGE_TITLE = "Registration Complete | Hawkeye";
const LOGIN_URL = "https://app.neubird.ai";
const API_URL = process.env.REACT_APP_API_URL || "";
const POLL_INTERVAL_MS = 15000;
const MESSAGE_INTERVAL_MS = 4000;
const MAX_WAIT_MS = 30 * 60 * 1000;

const BIRD_PROGRESS_MESSAGES = [
  "Taking flight over your new workspace...",
  "Gathering a few twigs for the nest...",
  "Feathering the final setup details...",
  "Gliding through the last provisioning steps...",
  "Warming up the flock...",
  "Perching your workspace in the cloud...",
  "Checking the horizon before landing...",
  "Getting your NeuBird nest ready...",
  "Swooping through the final checks...",
  "Almost ready to land...",
];

const getStoredDeploymentStatus = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem("nb_registration_deployment");
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.warn("Unable to read deployment status from session storage", error);
    return null;
  }
};

const shuffleIndexes = (length) => {
  const indexes = Array.from({ length }, (_, index) => index);
  for (let i = indexes.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [indexes[i], indexes[j]] = [indexes[j], indexes[i]];
  }
  return indexes;
};

const useBirdProgressMessage = () => {
  const orderRef = useRef(null);
  const [index, setIndex] = useState(0);

  if (!orderRef.current) {
    orderRef.current = shuffleIndexes(BIRD_PROGRESS_MESSAGES.length);
  }

  useEffect(() => {
    const id = window.setInterval(() => {
      setIndex((current) => (current + 1) % BIRD_PROGRESS_MESSAGES.length);
    }, MESSAGE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, []);

  return BIRD_PROGRESS_MESSAGES[
    orderRef.current[index % orderRef.current.length]
  ];
};

const SuccessPage = () => {
  const location = useLocation();
  const deploymentStatus = useMemo(
    () => location.state || getStoredDeploymentStatus(),
    [location.state]
  );
  const [status, setStatus] = useState("provisioning");
  const [message, setMessage] = useState(
    "Provisioning your NeuBird workspace. This usually takes a few minutes."
  );
  const birdMessage = useBirdProgressMessage();

  useEffect(() => {
    if (typeof window === "undefined") return;

    document.title = COMPLETE_PAGE_TITLE;

    const skipTracking = window.__skipTracking || sessionStorage.getItem("nb_skip_tracking");
    if (skipTracking) {
      console.log("[Hawkeye Registration] Skip tracking active – completion page view not sent");
      return;
    }

    const pageViewParams = {
      page_path: COMPLETE_PAGE_PATH,
      page_title: COMPLETE_PAGE_TITLE,
    };

    if (typeof window.gtag === "function") {
      window.gtag("event", "page_view", pageViewParams);
    }

    if (window.dataLayer) {
      window.dataLayer.push({
        event: "virtualPageView",
        ...pageViewParams,
      });
      window.dataLayer.push({ event: "gtm.historyChange" });
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const runId = deploymentStatus?.runId;
    const startedAt = deploymentStatus?.startedAt || Date.now();
    let cancelled = false;
    let timeoutId;

    if (!runId) {
      setStatus("waiting");
      setMessage(
        "Your workspace build is in flight. We will redirect you when it lands."
      );
      return undefined;
    }

    const pollStatus = async () => {
      try {
        const response = await axios.post(
          API_URL,
          { action: "deployment_status", run_id: runId },
          { headers: { "Content-Type": "application/json" } }
        );

        if (cancelled) return;

        const data = response.data || {};

        if (data.status === "ready") {
          setStatus("ready");
          setMessage("Your workspace is ready. Redirecting you to NeuBird...");
          sessionStorage.removeItem("nb_registration_deployment");
          window.setTimeout(() => {
            window.location.assign(
              data.login_url || deploymentStatus.loginUrl || LOGIN_URL
            );
          }, 1200);
          return;
        }

        if (data.status === "failed") {
          setStatus("failed");
          setMessage(
            "We could not finish provisioning your workspace. Our team has been notified."
          );
          return;
        }

        if (Date.now() - startedAt > MAX_WAIT_MS) {
          setStatus("delayed");
          setMessage(
            "Provisioning is taking longer than expected. You can leave this page open or sign in shortly."
          );
          return;
        }

        const workflowStatus = data.workflow_status || "queued";
        setStatus("provisioning");
        setMessage(
          workflowStatus === "in_progress"
            ? "Your workspace is in flight. We will redirect you when it lands."
            : "Your workspace is queued for takeoff. We will redirect you when it lands."
        );
      } catch (error) {
        if (cancelled) return;
        console.error("Deployment status polling error:", error);
        if (Date.now() - startedAt > MAX_WAIT_MS) {
          setStatus("delayed");
          setMessage(
            "Provisioning is taking longer than expected. You can leave this page open or sign in shortly."
          );
          return;
        }
        setStatus("provisioning");
        setMessage(
          "Still waiting for your workspace. We will keep checking automatically."
        );
      }

      timeoutId = window.setTimeout(pollStatus, POLL_INTERVAL_MS);
    };

    pollStatus();

    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [deploymentStatus]);

  const year = new Date().getFullYear();
  const showSpinner = status === "provisioning" || status === "waiting";
  const showLoginLink = status === "ready" || status === "delayed";
  const displayMessage = showSpinner ? birdMessage : message;

  return (
    <>
      <main className="success-page success-page--standalone">
        <div className="success-card">
          <div
            className={`success-icon ${showSpinner ? "success-icon--loading" : ""}`}
            aria-hidden="true"
          >
            {showSpinner ? "" : "\u2713"}
          </div>
          <h1 className="success-title">
            {status === "ready"
              ? "Your workspace is ready!"
              : "Setting up your workspace"}
          </h1>
          <p className="success-lead" role="status" aria-live="polite">
            {displayMessage}
          </p>
          <p>Keep this page open. You&apos;ll be redirected automatically.</p>
          {showLoginLink && (
            <a href={LOGIN_URL} className="cta-btn success-cta">
              Go to NeuBird
            </a>
          )}
          <p className="success-note">
            Need help getting started?{" "}
            <a
              href="https://docs.neubird.ai/platform/introduction/quick_start/"
              target="_blank"
              rel="noopener noreferrer"
            >
              View the quick start guide
            </a>
            {" "}or email{" "}
            <a href="mailto:support@neubird.ai">support@neubird.ai</a>.
          </p>
          <p className="privacy-line success-footer">
            &copy; {year} NeuBird AI, Inc. &middot;{" "}
            <a href="https://neubird.ai/privacy" target="_blank" rel="noopener noreferrer">Privacy</a>
            {" "}&middot;{" "}
            <a href="https://neubird.ai/terms" target="_blank" rel="noopener noreferrer">Terms</a>
            {" "}&middot;{" "}
            <a href="https://neubird.ai/security" target="_blank" rel="noopener noreferrer">Security</a>
            {" "}&middot;{" "}
            <a href="https://neubird.ai/contact" target="_blank" rel="noopener noreferrer">Contact</a>
          </p>
        </div>
      </main>
    </>
  );
};

export default SuccessPage;
